import axios from 'axios';
import {
	API_ENDPOINTS,
	BottomAnchoredControls,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	CardPlaceholder,
	customToast,
	FileHelpers,
	FormError,
	FormFieldDescription,
	humanFileSize,
	ImageType,
	PageLayout,
	replaceKeyWithValue,
	route,
	StringHelpers,
	SubTitle,
	UploadDataType,
	useDisabledContext,
	useForm,
	useLangContext,
	Variants,
	WidthConstrainedContainer,
} from 'carrier-fe';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CarouselMediaInput from '../components/CarouselMediaInput';

const MAX_CAROUSEL_FILE_SIZE = 50 * 1024 * 1024;

const defaultData = {
	image: undefined as ImageType | undefined,
	url: '',
};

type Data = typeof defaultData;

function CarouselSlideForm() {
	const { carouselSlideId } = useParams();
	const isEdit = !!carouselSlideId;

	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const navigate = useNavigate();

	const [isFetching, setIsFetching] = useState(false);
	const imageChanged = useRef(false);

	const modelName = crud?.models?.carousel_slide || 'Carousel Slide';

	const getUploadData = async () => {
		try {
			const response = await axios.get(
				route(API_ENDPOINTS.ADMIN.CAROUSEL_SLIDE.UPLOAD_URL)
			);
			return response?.data?.data;
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		}
	};

	const submit = async (payload: Data) => {
		let submitPayload: Partial<Data> = { ...payload };

		if (isEdit && !imageChanged.current) {
			delete submitPayload.image;
		}

		if (submitPayload.image) {
			delete submitPayload.image.base64;
		} else if (imageChanged.current) {
			(submitPayload as Record<string, unknown>).image = null;
		}

		return isEdit
			? axios.patch<{ data: Data; message: string }>(
					route(API_ENDPOINTS.ADMIN.CAROUSEL_SLIDE.UPDATE, {
						carouselSlide: String(carouselSlideId),
					}),
					submitPayload
			  )
			: axios.post<{ data: Data; message: string }>(
					route(API_ENDPOINTS.ADMIN.CAROUSEL_SLIDE.STORE),
					submitPayload
			  );
	};

	const { Input, Submit, setValues, data, store, errors } = useForm(
		submit,
		defaultData
	);

	useEffect(() => {
		if (isEdit) {
			fetchSlide();
		}
	}, []);

	const fetchSlide = async () => {
		if (isFetching) return;
		setIsFetching(true);
		try {
			const { data } = await axios.get<{ data: any }>(
				route(API_ENDPOINTS.ADMIN.CAROUSEL_SLIDE.EDIT, {
					carouselSlide: String(carouselSlideId),
				})
			);

			let image: ImageType | undefined;
			if (data.data.image) {
				const img = data.data.image;
				const extension = img.type
					? img.type.split('/').pop() || 'jpg'
					: 'jpg';
				image = {
					url: img.url,
					name: img.name || 'Image',
					extension,
					type: img.type || 'image/jpeg',
					size_bytes: img.size_bytes || 0,
					size_human:
						img.size_bytes > 0
							? humanFileSize(img.size_bytes)
							: 'Unknown',
					created_at: '',
					updated_at: '',
				};
			}

			setValues({
				url: data.data.url || '',
				image,
			});
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setIsFetching(false);
		}
	};

	const handleImageChange = async (fileData: ImageType | null) => {
		imageChanged.current = true;

		if (!fileData) {
			setValues({ image: undefined });
			return;
		}

		setDisabled(true);
		try {
			const uploadConfig: UploadDataType = await getUploadData();
			if (!uploadConfig) {
				throw new Error('Failed to get upload configuration');
			}

			const response = await FileHelpers.upload(
				fileData,
				uploadConfig,
				MAX_CAROUSEL_FILE_SIZE
			);

			if (response?.ok) {
				setValues({
					image: {
						...fileData,
						key: uploadConfig.key,
					},
				});
			} else {
				throw new Error('Failed to upload image to S3');
			}
		} catch (err: any) {
			customToast({
				title: err.message,
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
		}
	};

	useEffect(() => {
		if (data) {
			navigate('/carousel-slides');
		}
	}, [data, navigate]);

	let title = replaceKeyWithValue(
		crud?.pages?.create?.title || 'Create :model',
		'model',
		modelName
	);
	if (isEdit) {
		title = replaceKeyWithValue(
			crud?.pages?.edit?.title || 'Edit :model',
			'model',
			modelName
		);
	}

	return (
		<PageLayout title={StringHelpers.title(title)}>
			<WidthConstrainedContainer>
				{isFetching ? (
					<CardPlaceholder />
				) : (
					<Card>
						<CardBody className="pb-0">
							<SubTitle
								title={StringHelpers.title(
									crud?.sub_titles?.details || 'Details'
								)}
								className="mb-3"
								style={{ color: '#464C5E' }}
							/>

							<div className="mb-4">
								<CarouselMediaInput
									label={
										fields?.carousel_media ||
										'Image or video (JPG, PNG, GIF, MP4)'
									}
									value={store.image as ImageType}
									onChange={handleImageChange}
									errorField={!!errors?.image}
									disabled={disabled}
									onReject={(message) =>
										customToast({
											title: message,
											variant: Variants.Danger,
										})
									}
								/>
								<FormFieldDescription
									className="mt-1 text-end"
									description={
										fields?.carousel_max_file_size ||
										'Max file size: 50MB'
									}
								/>
								{!!errors?.image && (
									<FormError
										errorMessages={errors.image}
									/>
								)}
							</div>

							{Input({
								name: 'url',
								label: fields?.link_url || 'Link URL',
							})}
						</CardBody>
					</Card>
				)}
			</WidthConstrainedContainer>
			<BottomAnchoredControls>
				<Button
					label={crud?.buttons?.back?.default || 'Back'}
					onClick={() => navigate('/carousel-slides')}
					variant={Variants.Dark}
					type={ButtonTypes.Outline}
					className="me-4"
					disabled={disabled}
				/>
				<Submit
					defaultLabel={
						isEdit
							? crud?.buttons?.edit?.default || 'Update'
							: crud?.buttons?.create?.default || 'Create'
					}
					loadingLabel={
						isEdit
							? crud?.buttons?.edit?.submitting || 'Updating...'
							: crud?.buttons?.create?.submitting ||
							  'Creating...'
					}
					useOnMouseDown
					disabled={disabled || isFetching}
				/>
			</BottomAnchoredControls>
		</PageLayout>
	);
}

export default CarouselSlideForm;
